registerComponent("sections/contents-filterbar", function (component) {
/**
 * sections/contents-filterbar — recolhe o painel de filtros e mostra as setas do
 * trilho de editorias quando ele transborda.
 *
 * REGRA DE OURO, aplicada à risca aqui: **este script não filtra nada**. No
 * mockup ele filtrava — escondia cartões no cliente —, e cabia, porque a página
 * estática tinha 15 cartões. Aqui filtro é query no banco: as abas e os temas
 * são `<a>` de verdade e a busca é um `<form method="get">`. Sem JS, tudo
 * continua funcionando, com URL linkável e sem perder nada no refresh.
 *
 * O que sobra para o script é conforto puro:
 *
 *   · abrir e fechar o painel pelo botão "Filtros" (o botão já vem visível do
 *     Blade; quem o esconde sem JS é o `<noscript>` de lá);
 *   · as setas do trilho, que nascem `hidden` porque a rolagem por dedo e por
 *     roda já funciona sem elas;
 *   · o `<select>` de editorias de 1024 para baixo, que sem JS é um `<form>`
 *     com "Aplicar" — navegar no `change` é conforto, não o mecanismo.
 *
 * ⚠️ ESTE SCRIPT NÃO DECIDE O ESTADO INICIAL DE NADA. Ele decidia, e era o que
 * fazia a página piscar: o painel nascia aberto no HTML, o script o fechava
 * depois de carregar, e a tela mostrava busca e temas por um quadro antes de
 * engolir os dois. Agora o servidor emite o estado certo — fechado, ou aberto
 * quando há filtro ativo — e o script só reage a clique.
 *
 * O estado mora no `aria-expanded` do próprio botão, e não numa classe paralela:
 * a folha o lê com `:has(.fb-advanced[aria-expanded="true"])`. Um `aria-*` e uma
 * classe dizendo a mesma coisa saem de sincronia mais cedo ou mais tarde, e quem
 * sente é o leitor de tela.
 *
 * ⚠️ A EXCEÇÃO É O CELULAR, e ela tem uma razão só. Abaixo de 768 o painel é uma
 * folha de tela inteira, e uma folha que se abre sozinha na carga cobre a página
 * antes de a pessoa pedir. Só que o SERVIDOR emite `aria-expanded="true"` quando
 * há filtro na URL — de propósito, é o que faz a gaveta do desktop já chegar
 * aberta —, e o bundle é `defer`: se a folha lesse o atributo, ela apareceria na
 * primeira pintura e o script a fecharia depois, que é a piscada de sempre.
 *
 * Por isso o celular abre por `js-filtros-abertos`, uma classe que o SERVIDOR
 * NUNCA emite — é a diferença entre "veio aberto do servidor" e "a pessoa
 * abriu", e é justamente o que o atributo sozinho não sabe dizer. Ela é escrita
 * num lugar só (`definirPainel`), e o `aria-expanded` continua sendo o estado:
 * na partida, se o servidor mandou aberto e a largura é de celular, o script
 * fecha — sem nada a repintar, porque a folha nunca chegou a aparecer.
 */

const botao = component.querySelector('.fb-advanced');
const painel = component.querySelector('.adv-panel');
const fechar = component.querySelector('.adv-fechar');

// A largura em que o painel deixa de ser gaveta e vira folha de tela inteira.
// É o `@maxSm` da folha; um degrau só, escrito nos dois lugares porque o CSS não
// exporta media query para o JS.
const celular = matchMedia('(max-width: 768px)');

const definirPainel = (aberto) => {
    botao.setAttribute('aria-expanded', aberto ? 'true' : 'false');

    // O gancho do celular — ver a nota do cabeçalho. No desktop a folha ignora
    // esta classe e continua lendo o `aria-expanded`.
    component.classList.toggle('js-filtros-abertos', aberto);

    /*
     | `inert` tira o conteúdo recolhido da ordem de foco E da árvore de
     | acessibilidade. Sem ele, o painel some da tela (altura zero) mas o Tab
     | continua entrando nos campos invisíveis — o foco desaparece para quem
     | navega por teclado.
     */
    try {
        painel.inert = !aberto;
    } catch (e) {
        // Navegador sem `inert`: o painel recolhido segue focável. Degradação
        // aceitável — nada quebra, só não fica perfeito.
    }
};

if (botao && painel) {
    /*
     | ⚠️ O ESTADO INICIAL NÃO SE DECIDE AQUI — vem pronto do servidor, no
     | `aria-expanded` do botão (fechado, ou aberto quando há filtro ativo). Este
     | script antes o corrigia na carga, e era exatamente isso que piscava.
     |
     | O que falta é só o `inert`, que não tem como sair do HTML: ele não é
     | atributo que o Blade possa emitir com segurança em navegador que não o
     | suporta.
     */
    const temFiltro = component.querySelector('.active-chips') !== null;

    try {
        painel.inert = botao.getAttribute('aria-expanded') !== 'true';
    } catch (e) {
        // Navegador sem `inert`: segue focável, o que é degradação aceitável.
    }

    /*
     | ⚠️ NO CELULAR NUNCA COMEÇA ABERTO. O servidor manda `aria-expanded="true"`
     | quando há filtro na URL, e na folha de tela inteira isso seria a página
     | coberta antes de a pessoa pedir. Nada se repinta aqui: a folha só aparece
     | com a classe, que ainda não existe — o que este trecho corrige é o
     | ATRIBUTO, que estaria dizendo "aberto" com a folha fechada.
     |
     | Quem chega a esta largura pelo `resize` (girar um tablet, abrir o
     | inspetor) passa pelo mesmo caminho.
     */
    if (celular.matches) {
        definirPainel(false);
    }

    celular.addEventListener('change', (evento) => {
        if (evento.matches) definirPainel(false);
    });

    botao.addEventListener('click', () => {
        definirPainel(botao.getAttribute('aria-expanded') !== 'true');
        Asa.track('menu_toggle', { target_id: 'content_filters', state: botao.getAttribute('aria-expanded') === 'true' ? 'open' : 'close' });
    });

    // O X da folha de celular. `preventScroll` porque devolver o foco ao botão
    // "Filtros" com a página rolada faria o navegador saltar até ele.
    if (fechar) {
        fechar.addEventListener('click', () => {
            definirPainel(false);
            botao.focus({ preventScroll: true });
        });
    }

    // Clique fora fecha — mas não quando há filtro ativo, pelo mesmo motivo.
    document.addEventListener('click', (evento) => {
        if (temFiltro || botao.getAttribute('aria-expanded') !== 'true') return;
        if (component.contains(evento.target)) return;

        definirPainel(false);
    });

    document.addEventListener('keydown', (evento) => {
        if (evento.key !== 'Escape') return;
        if (botao.getAttribute('aria-expanded') !== 'true') return;

        definirPainel(false);
        botao.focus();
    });
}

/*
 | ── AS SETAS DO TRILHO DE EDITORIAS ────────────────────────────────────────
 | Seis editorias mais "Todos" não cabem abaixo de ~900: o trilho rola. As setas
 | só aparecem quando há transbordo de verdade, e se desabilitam nas pontas.
 */
const trilho = component.querySelector('.fb-tabs');
const navegacao = component.querySelector('.fb-tabnav');
const anterior = component.querySelector('.fb-prev');
const proxima = component.querySelector('.fb-next');

if (trilho && navegacao && anterior && proxima) {
    // 70% da largura visível: o suficiente para andar sem perder a referência
    // do que estava na borda. O piso de 160 evita passo ridículo em tela estreita.
    const passo = () => Math.max(160, Math.round(trilho.clientWidth * 0.7));

    const atualizar = () => {
        // A folga de 4px absorve o arredondamento subpixel do layout — sem ela,
        // um trilho que cabe por 0,3px acusa transbordo e pisca as setas.
        const transborda = trilho.scrollWidth - trilho.clientWidth > 4;

        navegacao.classList.toggle('has-overflow', transborda);
        anterior.hidden = !transborda;
        proxima.hidden = !transborda;

        if (!transborda) return;

        anterior.disabled = trilho.scrollLeft <= 1;
        proxima.disabled = trilho.scrollLeft + trilho.clientWidth >= trilho.scrollWidth - 1;
    };

    const rolar = (direcao) => {
        trilho.scrollBy({
            left: direcao * passo(),
            behavior: Asa.movimentoPermitido() ? 'smooth' : 'auto',
        });
    };

    anterior.addEventListener('click', () => rolar(-1));
    proxima.addEventListener('click', () => rolar(1));
    trilho.addEventListener('scroll', atualizar, { passive: true });
    window.addEventListener('resize', atualizar);

    atualizar();

    /*
     | A editoria ativa pode estar fora da janela do trilho quando a página abre
     | direto numa URL de categoria. Trazê-la para a vista é o que diz à pessoa
     | onde ela está. `block: 'nearest'` para não rolar a PÁGINA junto.
     */
    const ativa = trilho.querySelector('[aria-current]');

    if (ativa) {
        ativa.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'auto' });
        atualizar();
    }
}

/*
 | ── O `<select>` DE EDITORIAS (de 1024 para baixo) ─────────────────────────
 | Mesma regra de ouro: quem filtra é o servidor. O `<form method="get">` já
 | funciona sozinho pelo botão "Aplicar" — o script só o dispensa, navegando no
 | `change`.
 |
 | E navega pelo `data-url`, que é a URL BONITA da editoria, a mesma das abas:
 | o `submit` mandaria `?editoria=`, que o controller lê igual mas produz um
 | segundo endereço para a mesma página. Com JS as duas larguras chegam ao
 | mesmo lugar; sem JS, o `?editoria=` é o preço de continuar funcionando.
 */
const escolha = component.querySelector('.fb-pick');

if (escolha) {
    const seletor = escolha.querySelector('select');
    const aplicar = escolha.querySelector('.fb-pick-apply');

    if (seletor && aplicar) {
        aplicar.hidden = true;

        seletor.addEventListener('change', () => {
            const url = seletor.selectedOptions[0]?.dataset.url;

            if (url) {
                Asa.track('nav_click', { nav_area: 'content_categories', label: seletor.selectedOptions[0].textContent, destination: url, method: 'select' });
                window.location.assign(url);

                return;
            }

            escolha.submit();
        });
    }
}

});
